import { ApplicationRef, EnvironmentInjector, Injectable } from '@angular/core';
import { FlareClipboardElementAttributeWhitelist } from '@common/flare/constants/flare-clipboard-element-attribute-whitelist.constant';
import { FlareClipboardElementEmptyNodeWhitelist } from '@common/flare/constants/flare-clipboard-element-empty-node-whitelist.constant';
import { FlareClipboardElementStyleWhitelist } from '@common/flare/constants/flare-clipboard-element-style-whitelist.constant';
import { FlareCommands } from '@common/flare/flare-commands';
import { FlareSchema } from '@common/flare/flare-schema';
import { ProsemirrorFlareDOMParser } from '@common/flare/prosemirror-flare-dom-parser';
import { ProsemirrorFlareXMLSerializer } from '@common/flare/prosemirror-flare-xml-serializer';
import { flareClipboardElementEmptyNodeWhitelistMatcher } from '@common/flare/util/flare-clipboard-element-empty-node-whitelist-matcher';
import { baseKeymap } from '@common/prosemirror/commands/base-key-map';
import { AppliedConditionsPluginOptions, appliedConditionsPlugin } from '@common/prosemirror/plugins/applied-conditions.plugin';
import { clipboardImageResolverPlugin } from '@common/prosemirror/plugins/clipboard-image-resolver.plugin';
import { clipboardParserPlugin } from '@common/prosemirror/plugins/clipboard-parser.plugin';
import { clipboardSerializerPlugin } from '@common/prosemirror/plugins/clipboard-serializer.plugin';
import { emptyNodeRemovalPlugin } from '@common/prosemirror/plugins/empty-node-removal.plugin';
import { equationInsertPlugin } from '@common/prosemirror/plugins/equation-insert.plugin';
import { FlareComputedStylesheetPluginOptions, flareComputedStylesheetPlugin } from '@common/prosemirror/plugins/flare-computed-stylesheet.plugin';
import { guidAttrPastePlugin } from '@common/prosemirror/plugins/guid-attr-paste.plugin';
import { GutterPluginOptions, gutterPlugin } from '@common/prosemirror/plugins/gutter.plugin';
import { imageUploadPlugin } from '@common/prosemirror/plugins/image-upload.plugin';
import { MetaDataPluginOptions, metaDataPlugin } from '@common/prosemirror/plugins/meta-data';
import { MetaDataLoaderPluginOptions, metaDataLoaderPlugin } from '@common/prosemirror/plugins/meta-data-loader';
import { pasteCleanerPlugin } from '@common/prosemirror/plugins/paste-cleaner.plugin';
import { PasteNormalizerPluginOptions, pasteNormalizerPlugin } from '@common/prosemirror/plugins/paste-normalizer.plugin';
import { SelectionNormalizerPluginOptions, selectionNormalizerPlugin } from '@common/prosemirror/plugins/selection-normalizer.plugin';
import { slideshowPlugin } from '@common/prosemirror/plugins/slideshow.plugin';
import { tableDropRemovalPlugin } from '@common/prosemirror/plugins/table-drop-removal.plugin';
import { trackOverlappingReplacePlugin } from '@common/prosemirror/plugins/track-overlapping-replace.plugin';
import { trackedChangesCleanUpPlugin } from '@common/prosemirror/plugins/tracked-changes-clean-up';
import { trackedChangesMarkFixerPlugin } from '@common/prosemirror/plugins/tracked-changes-mark-fixer.plugin';
import { TrackedChangesPluginOptions, trackedChangesPlugin } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ViewPluginOptions, viewPlugin } from '@common/prosemirror/plugins/view.plugin';
import { vscodePasterPlugin } from '@common/prosemirror/plugins/vscode-paster.plugin';
import { toHashHexStringAsync } from '@common/util/crypto';
import { resolveCentralUrl, resolvePath } from '@common/util/path';
import { environment } from '@env/environment';
import { ApiService } from '@portal-core/auth/services/api.service';
import { Attachment } from '@portal-core/data/common/models/attachment.model';
import { FileService } from '@portal-core/general/services/file.service';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { MetaDataApiService } from '@portal-core/project-files/services/meta-data-api.service';
import { ReviewFilesService } from '@portal-core/reviews/review-files/services/review-files.service';
import { InsertImageEvent } from '@portal-core/text-editor/models/insert-image.event';
import { CodeSnippetBodyNodeView } from '@portal-core/text-editor/node-views/code-snippet-body.node-view';
import { DropDownHeadNodeView } from '@portal-core/text-editor/node-views/drop-down-head.node-view';
import { DropDownHotspotNodeView } from '@portal-core/text-editor/node-views/drop-down-hotspot.node-view';
import { DropDownNodeView } from '@portal-core/text-editor/node-views/drop-down.node-view';
import { EquationNodeView } from '@portal-core/text-editor/node-views/equation.node-view';
import { ExpandingHeadNodeView } from '@portal-core/text-editor/node-views/expanding-head.node-view';
import { ExpandingNodeView } from '@portal-core/text-editor/node-views/expanding.node-view';
import { FootnoteNodeView } from '@portal-core/text-editor/node-views/footnote.node-view';
import { GlossaryTermNodeView } from '@portal-core/text-editor/node-views/glossary-term.node-view';
import { HtmlNodeView } from '@portal-core/text-editor/node-views/html.node-view';
import { ImageNodeView } from '@portal-core/text-editor/node-views/image.node-view';
import { ContentNodeViewOptions } from '@portal-core/text-editor/node-views/models/content-node-view-options.model';
import { EditableNodeViewOptions } from '@portal-core/text-editor/node-views/models/editable-node-view-options.model';
import { SnippetNodeViewOptions } from '@portal-core/text-editor/node-views/models/snippet-node-view-options.model';
import { StyleLabelNodeViewOptions } from '@portal-core/text-editor/node-views/models/style-label-node-view-options.model';
import { MultipleChoiceItemNodeView } from '@portal-core/text-editor/node-views/multiple-choice-item.node-view';
import { PopupNodeView } from '@portal-core/text-editor/node-views/popup.node-view';
import { ShortcutNodeView } from '@portal-core/text-editor/node-views/shortcut.node-view';
import { SlideshowNodeView } from '@portal-core/text-editor/node-views/slideshow.node-view';
import { SnippetNodeView } from '@portal-core/text-editor/node-views/snippet.node-view';
import { StyleLabelNodeView } from '@portal-core/text-editor/node-views/style-label.node-view';
import { TagNameNodeView } from '@portal-core/text-editor/node-views/tag-name.node-view';
import { TogglerNodeView } from '@portal-core/text-editor/node-views/toggler.node-view';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { VariableNodeView } from '@portal-core/text-editor/node-views/variable.node-view';
import { EquationToolbarPopupPluginOptions, EquationToolbarPopupPluginView } from '@portal-core/text-editor/plugin-views/equation-toolbar-popup.plugin-view';
import { NodeToolbarPopupPluginOptions, NodeToolbarPopupPluginView } from '@portal-core/text-editor/plugin-views/node-toolbar-popup.plugin-view';
import { TextEditorApiService } from '@portal-core/text-editor/services/text-editor-api.service';
import { ElementOverlayService } from '@portal-core/ui/overlay/services/element.overlay.service';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { chainCommands, baseKeymap as pmBaseKeymap, toggleMark } from 'prosemirror-commands';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { ProseMirrorNode, Schema } from 'prosemirror-model';
import { liftListItem, splitListItem as pmSplitListItem, sinkListItem } from 'prosemirror-schema-list';
import { Command, Plugin } from 'prosemirror-state';
import { goToNextCell, tableEditing } from 'prosemirror-tables';
import { Decoration, EditorView, NodeViewConstructor } from 'prosemirror-view';
import { Observable } from 'rxjs';

export interface FlareNodeViewOptions {
  image?: ContentNodeViewOptions;
  madCapCodeSnippetBody?: EditableNodeViewOptions;
  madCapSnippet?: SnippetNodeViewOptions;
  madCapStyleLabel?: StyleLabelNodeViewOptions;
}

export interface FlarePluginOptions {
  computedStylesheet?: FlareComputedStylesheetPluginOptions;
  equationToolbarPopup?: EquationToolbarPopupPluginOptions;
  gutter?: GutterPluginOptions;
  metaData?: MetaDataPluginOptions;
  metaDataLoader?: MetaDataLoaderPluginOptions;
  nodeToolbarPopup?: NodeToolbarPopupPluginOptions;
  pasteNormalizer?: PasteNormalizerPluginOptions;
  trackedChanges?: TrackedChangesPluginOptions;
  clipboardImageResolver?: { onPastedImages: (imageInfos: InsertImageEvent[]) => void };
  view?: ViewPluginOptions;
  keymaps?: Dictionary<Command>;
  selectionNormalizer?: SelectionNormalizerPluginOptions;
  appliedConditions?: AppliedConditionsPluginOptions;
}

export interface RichTextPluginOptions {
  pasteNormalizer?: PasteNormalizerPluginOptions;
  view?: ViewPluginOptions;
}

// absolute path => commitId
export type SnippetCommitIdMap = Map<string, string>;

@Injectable({
  providedIn: 'root'
})
export class TextEditorService {
  constructor(
    private injector: EnvironmentInjector,
    private appRef: ApplicationRef,
    private apiService: ApiService,
    private elementOverlayService: ElementOverlayService,
    private textEditorApiService: TextEditorApiService,
    private fileService: FileService,
    private reviewFilesService: ReviewFilesService,
    private metaDataApiService: MetaDataApiService
  ) { }

  readonly flareTextEditorDocMaxCharLength: number = environment.flareTextEditorDocMaxCharLength ? parseInt(environment.flareTextEditorDocMaxCharLength, 10) : 0;

  createFlareNodeViews(schema: FlareSchema, nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>, editorTagNames: Record<string, string>, options: FlareNodeViewOptions = {}): Dictionary<NodeViewConstructor> {
    const nodeViews: Dictionary<NodeViewConstructor> = {};

    if (schema.nodes.html) {
      nodeViews.html = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new HtmlNodeView(node, editorView, getPos, decorations, nodeViewBridge, editorTagNames?.html);
      };
    }

    if (schema.nodes.body && editorTagNames?.body) {
      nodeViews.body = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new TagNameNodeView(node, editorView, getPos, decorations, nodeViewBridge, editorTagNames.body);
      };
    }

    if (schema.nodes.image) {
      nodeViews.image = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new ImageNodeView(node, editorView, getPos, decorations, nodeViewBridge, options.image);
      };
    }

    if (schema.nodes.madcapcodesnippetbody) {
      nodeViews.madcapcodesnippetbody = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new CodeSnippetBodyNodeView(node, editorView, getPos, decorations, nodeViewBridge, options.madCapCodeSnippetBody);
      };
    }

    if (schema.nodes.madcapcodesnippetcopybutton) {
      nodeViews.madcapcodesnippetcopybutton = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new StyleLabelNodeView(node, editorView, getPos, decorations, nodeViewBridge, { defaultLabel: 'Copy', ...options.madCapStyleLabel });
      };
    }

    if (schema.nodes.madcapdropdown) {
      nodeViews.madcapdropdown = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new DropDownNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapdropdownhead) {
      nodeViews.madcapdropdownhead = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new DropDownHeadNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapdropdownhotspot) {
      nodeViews.madcapdropdownhotspot = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new DropDownHotspotNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapequation) {
      nodeViews.madcapequation = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new EquationNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapexpanding) {
      nodeViews.madcapexpanding = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new ExpandingNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapexpandinghead) {
      nodeViews.madcapexpandinghead = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new ExpandingHeadNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapfootnote) {
      nodeViews.madcapfootnote = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new FootnoteNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapglossaryterm) {
      nodeViews.madcapglossaryterm = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new GlossaryTermNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.math) {
      nodeViews.math = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new EquationNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.mmlmath) {
      nodeViews.mmlmath = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new EquationNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapmultiplechoiceitem) {
      nodeViews.madcapmultiplechoiceitem = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new MultipleChoiceItemNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcappopup) {
      nodeViews.madcappopup = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new PopupNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapshortcut) {
      nodeViews.madcapshortcut = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new ShortcutNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapslideshow) {
      nodeViews.madcapslideshow = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new SlideshowNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapsnippetblock) {
      nodeViews.madcapsnippetblock = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new SnippetNodeView(node, editorView, getPos, decorations, nodeViewBridge, this.injector, this.appRef, this.reviewFilesService, this.textEditorApiService, options.madCapSnippet);
      };
    }

    if (schema.nodes.madcapsnippettext) {
      nodeViews.madcapsnippettext = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new SnippetNodeView(node, editorView, getPos, decorations, nodeViewBridge, this.injector, this.appRef, this.reviewFilesService, this.textEditorApiService, options.madCapSnippet);
      };
    }

    if (schema.nodes.madcapsubmitquestionbutton) {
      nodeViews.madcapsubmitquestionbutton = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new StyleLabelNodeView(node, editorView, getPos, decorations, nodeViewBridge, { defaultLabel: 'Submit', ...options.madCapStyleLabel });
      };
    }

    if (schema.nodes.madcaptoggler) {
      nodeViews.madcaptoggler = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new TogglerNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    if (schema.nodes.madcapvariable) {
      nodeViews.madcapvariable = (node: ProseMirrorNode, editorView: EditorView, getPos: GetPosForNodeView, decorations: Decoration[]) => {
        return new VariableNodeView(node, editorView, getPos, decorations, nodeViewBridge);
      };
    }

    return nodeViews;
  }

  createFlarePlugins(schema: FlareSchema, options: FlarePluginOptions = {}, extraPlugins?: Plugin[]): Plugin[] {
    const flareCommands = new FlareCommands(schema);
    const plugins: Plugin[] = [];

    options.keymaps ? plugins.push(keymap(options.keymaps)) : null;
    plugins.push(
      history(),
      // Lists
      keymap({
        'Enter': chainCommands(
          flareCommands.splitListItem,
          flareCommands.splitDefinitionListItem,
          flareCommands.appendMultipleChoiceItem
        ),
        'Tab': flareCommands.indentAnyListItem,
        'Shift-Tab': flareCommands.outdentAnyListItem
      }),
      // Dropdowns
      keymap({
        'Enter': flareCommands.moveFromDropDown
      }),
      // Tables
      keymap({
        'Tab': goToNextCell(1),
        'Shift-Tab': goToNextCell(-1)
      }),
      keymap({
        'Mod-z': undo,
        'Mod-y': redo
      }),
      keymap({
        'Shift-Enter': flareCommands.insertBr
      }),
      // Typography key bindings
      keymap({
        'Mod-b': flareCommands.toggleBold,
        'Mod-B': flareCommands.toggleBold,
        'Mod-i': flareCommands.toggleItalics,
        'Mod-I': flareCommands.toggleItalics,
        'Mod-u': flareCommands.toggleUnderline
      }),
      keymap(baseKeymap),
      gapCursor(),
      // Clipboard
      clipboardSerializerPlugin({
        clipboardSerializer: new ProsemirrorFlareXMLSerializer(schema)
      }),
      clipboardParserPlugin({
        clipboardParser: ProsemirrorFlareDOMParser.fromSchema(schema)
      }),
      pasteNormalizerPlugin({
        attributesWhitelist: FlareClipboardElementAttributeWhitelist,
        emptyNodeWhitelist: FlareClipboardElementEmptyNodeWhitelist,
        emptyNodeWhitelistMatcher: flareClipboardElementEmptyNodeWhitelistMatcher,
        pageBreakReplacement: { tagName: 'MadCap:pageBreak' },
        styleWhitelist: FlareClipboardElementStyleWhitelist,
        ...options.pasteNormalizer
      }),
      vscodePasterPlugin({
        codeNodeTypesOrNames: ['madcapcodesnippet', 'madcapcodesnippetbody']
      }),
      guidAttrPastePlugin()
    );

    plugins.push(emptyNodeRemovalPlugin());

    if (options.metaData) {
      plugins.push(metaDataPlugin(options.metaData));
    }

    if (options.metaDataLoader) {
      plugins.push(metaDataLoaderPlugin(options.metaDataLoader));
    }

    // Always include the tracked change mark fixer plugin even when trackedChanges are not enabled because tracked changes can pre-exist in the doc and this plugin could be needed to fix them.
    // trackedChangesMarkFixerPlugin must come before trackedChangesPlugin
    plugins.push(trackedChangesMarkFixerPlugin());
    if (options.trackedChanges) {
      plugins.push(trackOverlappingReplacePlugin());
      // trackedChangesPlugin must come after trackOverlappingReplacePlugin
      plugins.push(trackedChangesPlugin(options.trackedChanges));
      // trackedChangesCleanUpPlugin must come after trackedChangesPlugin
      plugins.push(trackedChangesCleanUpPlugin());
    }

    if (options.clipboardImageResolver) {
      plugins.push(clipboardImageResolverPlugin({
        imageType: schema.nodes.image,
        onPastedImages: options.clipboardImageResolver.onPastedImages,
        imagePlaceholdersCommand: flareCommands.insertImagePlaceholders,
        getFile: this.fileService.convertBase64ImageToFile
      }));
    }

    if (options.nodeToolbarPopup && options.view) {
      plugins.push(viewPlugin(new NodeToolbarPopupPluginView(this.elementOverlayService, options.nodeToolbarPopup), options.view));
    }

    if (options.equationToolbarPopup && options.view) {
      plugins.push(viewPlugin(new EquationToolbarPopupPluginView(this.elementOverlayService, options.equationToolbarPopup), options.view));
    }

    if (options.gutter) {
      plugins.push(gutterPlugin(options.gutter));
    }

    plugins.push(slideshowPlugin());
    plugins.push(tableDropRemovalPlugin());
    plugins.push(imageUploadPlugin());
    plugins.push(equationInsertPlugin());

    if (options.selectionNormalizer) {
      plugins.push(selectionNormalizerPlugin(options.selectionNormalizer));
    }

    if (options.computedStylesheet) {
      plugins.push(flareComputedStylesheetPlugin(options.computedStylesheet));
    }

    if (extraPlugins?.length > 0) {
      plugins.push(...extraPlugins);
    }

    // The applied conditions plugin should go near the end because its decorations wrap nodes which causes any subsequent plugin decorations to be applied to the wrapper
    if (options.appliedConditions) {
      plugins.push(appliedConditionsPlugin(options.appliedConditions));
    }

    // prosemirror-tables suggests using this plugin last because of how it handles mouse and arrow key events
    plugins.push(tableEditing({ allowTableNodeSelection: true }));

    return plugins;
  }

  createRichTextPlugins(schema: Schema, options?: RichTextPluginOptions): Plugin[] {
    const plugins: Plugin[] = [];

    plugins.push(
      history(),
      // List key bindings
      keymap({
        'Enter': pmSplitListItem(schema.nodes.list_item),
        'Tab': sinkListItem(schema.nodes.list_item),
        'Shift-Tab': liftListItem(schema.nodes.list_item)
      }),
      // History key bindings
      keymap({
        'Mod-z': undo,
        'Mod-y': redo
      }),
      // Hard break key binding
      keymap({
        'Shift-Enter': (state, dispatch) => {
          dispatch(state.tr.replaceSelectionWith(schema.nodes.hard_break.create()).scrollIntoView());
          return true;
        }
      }),
      // Typography key bindings
      keymap({
        'Mod-b': toggleMark(schema.marks.strong),
        'Mod-B': toggleMark(schema.marks.strong),
        'Mod-i': toggleMark(schema.marks.em),
        'Mod-I': toggleMark(schema.marks.em),
        'Mod-u': toggleMark(schema.marks.u)
      }),
      keymap(pmBaseKeymap),
      gapCursor(),
      pasteCleanerPlugin({
        ignoredNodeTypesOrNames: ['image']
      }),
      pasteNormalizerPlugin(options.pasteNormalizer),
      vscodePasterPlugin({
        codeNodeTypesOrNames: ['code_block']
      })
    );

    return plugins;
  }

  createRichTextNodeViews(): Dictionary<NodeViewConstructor> {
    return {};
  }

  uploadImage$(fileId: number, image: FormData): Observable<Attachment> {
    return this.textEditorApiService.uploadImage$(fileId, image);
  }

  /**
   * Returns the URI for content that is referenced from a flare document (eg an image or snippet.)
   * This functions expects two paths:
   * The path of the file that contains the content.
   * And the path of the content as defined in that file.
   * If the path of the content is relative to the file then this function will resolve that path on its own.
   * For example, if the URI is needed for an image then the contentPath is the value of the src attribute on the image tag.
   * @param projectId The project id that the file belongs to.
   * @param commitIdOrBranchName  The commit id or branch name that the file exists in.
   * @param filePath The path of the file that the content is referenced from.
   * @param contentPath The path of the content being referenced from the file. This is the content path as defined within the file.
   */
  buildFlareContentApiUri(projectId: number, commitIdOrBranchName: string, filePath: string, contentPath: string): string {
    return `${this.apiService.centralContentBaseUri}/${projectId}/${commitIdOrBranchName}/${resolvePath(filePath, contentPath)}`;
  }

  async buildComputedStylesheetClassNameAsync(filePath: string): Promise<string> {
    const hex = await toHashHexStringAsync(filePath);
    return `mc-computed-stylesheet-${hex}`;
  }

  buildComputedStylesheetId(filePathHex: string): string {
    return `mc-computed-stylesheet-${filePathHex}`;
  }

  async buildFilePathHex(filePath: string): Promise<string> {
    return toHashHexStringAsync(filePath);
  }

  buildPreviewStylesheetContainerId(filePathHex: string): string {
    return `mc-preview-stylesheet-container-${filePathHex}`;
  }

  buildPreviewStylesheetId(filePathHex: string): string {
    return `mc-preview-stylesheet-${filePathHex}`;
  }

  /**
   * Resolves a URL using the central protocol to a URL that contains the Central instance in the path.
   * @param url The URL to resolve.
   * @returns The resolved URL.
   */
  resolveCentralUrl(url: string): string {
    return resolveCentralUrl(url, this.apiService.centralApiBaseUri);
  }

  getFlareSnippet$(snippetContentApiUri: string): Observable<string> {
    return this.textEditorApiService.getFlareSnippet$(snippetContentApiUri);
  }
}
